import { useNavigate } from "react-router";
import { Bath, BedDouble, Building2, Camera, Coins, ImageIcon, Sofa, Square } from "lucide-react";

import { cn } from "@/lib/utils";
import { PropertyStatusBadge } from "@/features/property/components/property-status-badge";
import { PropertyFreshnessBadge } from "@/features/property/components/property-freshness-badge";
import { usePrefetchProperty } from "@/features/property/api";
import { useOrganization } from "@/features/settings/api";
import { formatPrice } from "@/features/property/utils/format-price";
import { formatRelativeVi } from "@/features/property/utils/relative-time";
import { propertyFurnitureLabel } from "@/features/property/utils/property-enum-options";
import { formatBrokerageFee } from "@/features/property/utils/property-display";
import type { Property } from "@/types/property";

interface PropertyCardProps {
  property: Property;
}

const MD_BREAKPOINT_PX = 768;

function isHoverViewport(): boolean {
  if (typeof window === "undefined") return false;
  return window.matchMedia(`(min-width: ${MD_BREAKPOINT_PX}px) and (hover: hover)`).matches;
}

export function PropertyCard({ property }: PropertyCardProps) {
  const navigate = useNavigate();
  const prefetch = usePrefetchProperty();
  const orgQuery = useOrganization();
  const staleDays = orgQuery.data?.freshnessStaleDays ?? 14;

  const openDetail = () => navigate(`/properties/${property.id}`);
  const onKeyActivate = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      openDetail();
    }
  };
  const handleHoverPrefetch = () => {
    if (isHoverViewport()) prefetch(property.id);
  };

  const buildingName = property.buildingName ?? "Bất động sản";
  const wardLine = property.wardDisplayName;
  // Same spec set as the detail's SpecsGrid; furniture + fee are words, so they
  // render outside the tabular-nums run.
  const brokerageFee = formatBrokerageFee(
    property.brokerageFeeType,
    property.brokerageFeeValue,
  );

  return (
    <div
      role="link"
      tabIndex={0}
      aria-label={`${buildingName} — ${property.wardDisplayName}`}
      onClick={openDetail}
      onKeyDown={onKeyActivate}
      onMouseEnter={handleHoverPrefetch}
      onFocus={() => prefetch(property.id)}
      className={cn(
        "group flex cursor-pointer flex-row overflow-hidden rounded-xl border border-border bg-card transition-all sm:flex-col",
        "shadow-[var(--shadow-soft)] md:hover:border-border-strong md:hover:shadow-[var(--shadow-card)]",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
      )}
    >
      <div className="relative aspect-square w-28 shrink-0 overflow-hidden bg-muted sm:aspect-[16/10] sm:w-full">
        {property.thumbnailUrl ? (
          <img
            src={property.thumbnailUrl}
            alt=""
            loading="lazy"
            className="h-full w-full object-cover"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-ink-faint">
            <ImageIcon className="size-8 sm:size-10" aria-hidden="true" />
          </div>
        )}

        <div className="absolute top-1.5 left-1.5 flex gap-1 sm:top-2 sm:left-2 sm:gap-1.5">
          {property.internalCode ? (
            <span className="rounded bg-foreground/85 px-1.5 py-0.5 font-mono text-[11px] font-medium text-background backdrop-blur-sm">
              {property.internalCode}
            </span>
          ) : null}
          <PropertyFreshnessBadge
            lastFreshnessConfirmedAt={property.lastFreshnessConfirmedAt}
            staleDays={staleDays}
          />
        </div>

        {property.imageCount > 1 ? (
          <div className="absolute bottom-1.5 left-1.5 inline-flex items-center gap-1 rounded-full bg-foreground/60 px-1.5 py-0.5 font-mono text-[11px] text-background backdrop-blur-sm sm:bottom-auto sm:left-auto sm:top-2 sm:right-2 sm:px-2">
            <Camera className="size-3" aria-hidden="true" />
            <span>{property.imageCount}</span>
          </div>
        ) : null}
      </div>

      <div className="flex min-w-0 flex-1 flex-col gap-1.5 p-3 sm:gap-2 sm:p-3.5">
        <div className="flex items-start justify-between gap-2">
          <h3 className="min-w-0 flex-1 truncate text-sm font-semibold leading-tight">
            {buildingName}
          </h3>
          <PropertyStatusBadge
            status={property.status}
            listingType={property.listingType}
            className="shrink-0"
          />
        </div>

        <div className="truncate text-[12px] text-muted-foreground">
          {wardLine}
        </div>

        <div className="grid grid-cols-3 items-center gap-x-2 gap-y-1.5 text-[11px] text-ink-soft">
          <span className="inline-flex items-center gap-1 font-mono tabular-nums">
            <Square aria-hidden="true" className="size-3" />
            {property.area}m²
          </span>
          {property.bedrooms !== null ? (
            <span className="inline-flex items-center gap-1 font-mono tabular-nums">
              <BedDouble aria-hidden="true" className="size-3" />
              {property.bedrooms}
            </span>
          ) : null}
          {property.bathrooms !== null ? (
            <span className="inline-flex items-center gap-1 font-mono tabular-nums">
              <Bath aria-hidden="true" className="size-3" />
              {property.bathrooms}
            </span>
          ) : null}
          {property.floor !== null ? (
            <span className="inline-flex items-center gap-1 font-mono tabular-nums">
              <Building2 aria-hidden="true" className="size-3" />
              Tầng {property.floor}
            </span>
          ) : property.floorBand !== null ? (
            <span className="inline-flex items-center gap-1">
              <Building2 aria-hidden="true" className="size-3" />
              {property.floorBand}
            </span>
          ) : null}
          {property.furniture !== null ? (
            <span className="inline-flex items-center gap-1">
              <Sofa aria-hidden="true" className="size-3" />
              {propertyFurnitureLabel(property.furniture)}
            </span>
          ) : null}
          {brokerageFee !== null ? (
            <span className="inline-flex items-center gap-1">
              <Coins aria-hidden="true" className="size-3" />
              {brokerageFee}
            </span>
          ) : null}
        </div>

        <div className="mt-auto flex items-baseline justify-between gap-2 pt-2 sm:border-t sm:border-border sm:pt-2.5">
          <span className="font-mono text-[16px] font-semibold tabular-nums tracking-tight sm:text-[16px]">
            {formatPrice(property.price, property.listingType)}
          </span>
          <span className="shrink-0 text-[11px] text-ink-faint">
            {formatRelativeVi(property.updatedAt)}
          </span>
        </div>
      </div>
    </div>
  );
}
